import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  GridLegacy as Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  DirectionsCar as CarIcon,
  TwoWheeler as MotorcycleIcon,
  LocalShipping as TruckIcon
} from '@mui/icons-material';
import './VehicleTypes.css';

interface VehicleType {
  _id?: string;
  name: string;
  code: string;
  description: string;
  maxAmount: number;
  isDefault: boolean;
  isActive: boolean;
  icon: string;
  createdAt?: string;
  updatedAt?: string;
}

const VehicleTypes: React.FC = () => {
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingType, setEditingType] = useState<VehicleType | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [formData, setFormData] = useState<VehicleType>({
    name: '',
    code: '',
    description: '',
    maxAmount: 0,
    isDefault: false,
    isActive: true,
    icon: 'car'
  });

  // 圖標選項
  const iconOptions = [
    { value: 'car', label: '小車', icon: <CarIcon /> },
    { value: 'motorcycle', label: '機車', icon: <MotorcycleIcon /> },
    { value: 'truck', label: '卡車', icon: <TruckIcon /> },
    { value: 'van', label: '廂型車', icon: <CarIcon /> }
  ];

  // 模擬數據
  const mockVehicleTypes: VehicleType[] = [
    {
      _id: '1',
      name: '小車',
      code: 'CAR',
      description: '一般小客車',
      maxAmount: 0,
      isDefault: true,
      isActive: true,
      icon: 'car',
      createdAt: '2024-01-01T00:00:00Z'
    },
    {
      _id: '2',
      name: '機車',
      code: 'MOTORCYCLE',
      description: '機車類型',
      maxAmount: 0,
      isDefault: false,
      isActive: true,
      icon: 'motorcycle',
      createdAt: '2024-01-01T00:00:00Z'
    },
    {
      _id: '3',
      name: '大車',
      code: 'TRUCK',
      description: '大型車輛',
      maxAmount: 0,
      isDefault: false,
      isActive: true,
      icon: 'truck',
      createdAt: '2024-01-01T00:00:00Z'
    },
    {
      _id: '4',
      name: '超大車',
      code: 'LARGE_TRUCK',
      description: '超大型車輛',
      maxAmount: 0,
      isDefault: false,
      isActive: true,
      icon: 'truck',
      createdAt: '2024-01-01T00:00:00Z'
    }
  ];

  useEffect(() => {
    loadVehicleTypes();
  }, []);

  const loadVehicleTypes = async () => {
    try {
      setLoading(true);
      // 這裡應該調用 API，現在使用模擬數據
      setTimeout(() => {
        setVehicleTypes(mockVehicleTypes);
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('載入車輛類型失敗:', error);
      setSnackbar({ open: true, message: '載入車輛類型失敗', severity: 'error' });
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof VehicleType, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOpenDialog = (type?: VehicleType) => {
    if (type) {
      setEditingType(type);
      setFormData({ ...type });
    } else {
      setEditingType(null);
      setFormData({
        name: '',
        code: '',
        description: '',
        maxAmount: 0,
        isDefault: false,
        isActive: true,
        icon: 'car'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingType(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      maxAmount: 0,
      isDefault: false,
      isActive: true,
      icon: 'car'
    });
  };

  const handleSave = async () => {
    try {
      // 表單驗證
      if (!formData.name.trim()) {
        setSnackbar({ open: true, message: '請輸入車輛類型名稱', severity: 'error' });
        return;
      }

      if (!formData.code.trim()) {
        setSnackbar({ open: true, message: '請輸入車輛類型代碼', severity: 'error' });
        return;
      }

      // 檢查代碼是否重複
      const existingType = vehicleTypes.find(type => 
        type.code === formData.code && type._id !== editingType?._id
      );
      if (existingType) {
        setSnackbar({ open: true, message: '車輛類型代碼已存在', severity: 'error' });
        return;
      }

      setLoading(true);

      if (editingType) {
        // 更新現有類型
        const updatedTypes = vehicleTypes.map(type =>
          type._id === editingType._id ? { ...formData, _id: editingType._id } : type
        );
        setVehicleTypes(updatedTypes);
        setSnackbar({ open: true, message: '車輛類型更新成功', severity: 'success' });
      } else {
        // 新增類型
        const newType: VehicleType = {
          ...formData,
          _id: Date.now().toString(),
          createdAt: new Date().toISOString()
        };
        setVehicleTypes(prev => [...prev, newType]);
        setSnackbar({ open: true, message: '車輛類型新增成功', severity: 'success' });
      }

      handleCloseDialog();
    } catch (error) {
      console.error('保存車輛類型失敗:', error);
      setSnackbar({ open: true, message: '保存車輛類型失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('確定要刪除這個車輛類型嗎？')) {
      try {
        setLoading(true);
        const updatedTypes = vehicleTypes.filter(type => type._id !== id);
        setVehicleTypes(updatedTypes);
        setSnackbar({ open: true, message: '車輛類型刪除成功', severity: 'success' });
      } catch (error) {
        console.error('刪除車輛類型失敗:', error);
        setSnackbar({ open: true, message: '刪除車輛類型失敗', severity: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  const getIconComponent = (iconName: string) => {
    const iconOption = iconOptions.find(opt => opt.value === iconName);
    return iconOption ? iconOption.icon : <CarIcon />;
  };

  return (
    <Box className="vehicle-types-container">
      <Box className="vehicle-types-header">
        <Typography variant="h4" component="h1" className="page-title">
          車輛類型信息
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          className="add-button"
        >
          添加
        </Button>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" my={3}>
          <CircularProgress />
        </Box>
      )}

      <Paper className="table-container">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>車輛類型</TableCell>
                <TableCell>代碼</TableCell>
                <TableCell>描述</TableCell>
                <TableCell>每天最大收費金額（元）</TableCell>
                <TableCell>是否默認</TableCell>
                <TableCell>狀態</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicleTypes.map((type) => (
                <TableRow key={type._id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getIconComponent(type.icon)}
                      {type.name}
                    </Box>
                  </TableCell>
                  <TableCell>{type.code}</TableCell>
                  <TableCell>{type.description}</TableCell>
                  <TableCell>{type.maxAmount}</TableCell>
                  <TableCell>
                    {type.isDefault ? (
                      <Chip label="是" color="primary" size="small" />
                    ) : (
                      <Chip label="否" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={type.isActive ? '啟用' : '停用'}
                      color={type.isActive ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(type)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(type._id!)}
                      size="small"
                      disabled={type.isDefault}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 新增/編輯對話框 */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingType ? '編輯車輛類型' : '增加車輛類型'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="車輛類型"
                required
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="請輸入車輛類型名稱"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="代碼"
                required
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                placeholder="請輸入車輛類型代碼"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>圖標</InputLabel>
                <Select
                  value={formData.icon}
                  label="圖標"
                  onChange={(e) => handleInputChange('icon', e.target.value)}
                >
                  {iconOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {option.icon}
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="描述"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="請輸入車輛類型描述"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="臨時卡每天最大額"
                type="number"
                value={formData.maxAmount}
                onChange={(e) => handleInputChange('maxAmount', parseInt(e.target.value) || 0)}
                InputProps={{
                  inputProps: { min: 0 }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box display="flex" flexDirection="column" gap={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isDefault}
                      onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                    />
                  }
                  label="是否默認"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    />
                  }
                  label="啟用狀態"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
            取消
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            確定
          </Button>
        </DialogActions>
      </Dialog>

      {/* 通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VehicleTypes;